import type { PriceOption, PricePart } from '../../product';

/**
 * 判断两个 KeyValue 对象是否相同
 * @param kv1 - 第一个 KeyValue 对象
 * @param kv2 - 第二个 KeyValue 对象
 * @returns 如果两个对象完全相同返回 true，否则返回 false
 */
export const areKeyValuesEqual = (
  kv1: Record<string, any> | null | undefined,
  kv2: Record<string, any> | null | undefined
): boolean => {
  // 处理 null 或 undefined 情况
  if (!kv1 && !kv2) return true;
  if (!kv1 || !kv2) return false;

  // 比较对象的键值对
  const keys1 = Object.keys(kv1);
  const keys2 = Object.keys(kv2);

  if (keys1.length !== keys2.length) return false;

  return keys1.every(
    key =>
      Object.prototype.hasOwnProperty.call(kv2, key) && kv1[key] === kv2[key]
  );
};

/**
 * 判断两个 KeyValue 数组是否相同
 * @param kvArray1 - 第一个 KeyValue 数组
 * @param kvArray2 - 第二个 KeyValue 数组
 * @returns 如果两个数组内容完全相同返回 true，否则返回 false
 */
export const areKeyValueArraysEqual = (
  kvArray1: Record<string, any>[] | null | undefined,
  kvArray2: Record<string, any>[] | null | undefined
): boolean => {
  // 处理 null 或 undefined 情况
  if (!kvArray1 && !kvArray2) return true;
  if (!kvArray1 || !kvArray2) return false;

  // 长度不同，直接返回 false
  if (kvArray1.length !== kvArray2.length) return false;

  // 比较每个元素
  return kvArray1.every((kv1, index) =>
    areKeyValuesEqual(kv1, kvArray2[index])
  );
};

/**
 * 判断两个价格选项是否相同，忽略uuid属性
 * @param option1 - 第一个价格选项
 * @param option2 - 第二个价格选项
 * @returns 如果两个价格选项除uuid外完全相同返回 true，否则返回 false
 */
export const arePriceOptionsEqual = (
  option1: PriceOption | undefined | null,
  option2: PriceOption | undefined | null
): boolean => {
  // 处理 null 或 undefined 情况
  if (!option1 && !option2) return true;
  if (!option1 || !option2) return false;

  // 明确比较 PriceOption 的每个字段，忽略 uuid
  return (
    Boolean(option1.use_custom_quantity) ===
      Boolean(option2.use_custom_quantity) &&
    String(option1.custom_quantity_unit) ===
      String(option2.custom_quantity_unit) &&
    areKeyValueArraysEqual(option1.tiered_prices, option2.tiered_prices) &&
    Number(option1.price) === Number(option2.price) &&
    Boolean(option1.use_minimum_price) === Boolean(option2.use_minimum_price) &&
    Boolean(option1.use_tiered_price) === Boolean(option2.use_tiered_price) &&
    option1.minimum_price === option2.minimum_price &&
    option1.minimum_cost === option2.minimum_cost &&
    option1.tiered_price_method == option2.tiered_price_method &&
    Number(option1.cost) === Number(option2.cost) &&
    String(option1.name || '') === String(option2.name || '') &&
    String(option1.sku || '') === String(option2.sku || '')
    // 注意：我们故意忽略了 uuid 字段
  );
};

/**
 * 判断两个价格选项数组是否相同，忽略uuid属性
 * @param options1 - 第一个价格选项数组
 * @param options2 - 第二个价格选项数组
 * @returns 如果两个数组内容除uuid外完全相同返回 true，否则返回 false
 */
export const arePriceOptionsArrayEqual = (
  options1: PriceOption[] | undefined | null,
  options2: PriceOption[] | undefined | null
): boolean => {
  // 处理 null 或 undefined 情况
  if (!options1 && !options2) return true;
  if (!options1 || !options2) return false;

  // 长度不同，直接返回 false
  if (options1.length !== options2.length) return false;

  // 比较每个选项
  return options1.every((option1, index) =>
    arePriceOptionsEqual(option1, options2[index])
  );
};

/**
 * 判断两个价格部分是否相同，忽略uuid属性
 * @param part1 - 第一个价格部分
 * @param part2 - 第二个价格部分
 * @returns 如果两个价格部分除uuid外完全相同返回 true，否则返回 false
 */
export const arePricePartsEqual = (
  part1: PricePart | undefined | null,
  part2: PricePart | undefined | null
): boolean => {
  // 处理 null 或 undefined 情况
  if (!part1 && !part2) return true;
  if (!part1 || !part2) return false;

  // 比较基本属性
  const areBasicPropsEqual =
    part1.mandatory === part2.mandatory &&
    part1.name === part2.name &&
    part1.order === part2.order &&
    part1.optional === part2.optional;

  // 如果基本属性不相等，直接返回 false
  if (!areBasicPropsEqual) return false;

  // 比较价格选项数组
  return arePriceOptionsArrayEqual(part1.price_options, part2.price_options);
};

/**
 * 判断两个价格部分数组是否相同，忽略uuid属性
 * @param parts1 - 第一个价格部分数组
 * @param parts2 - 第二个价格部分数组
 * @returns 如果两个数组内容除uuid外完全相同返回 true，否则返回 false
 */
export const arePricePartsArrayEqual = (
  parts1: PricePart[] | undefined | null,
  parts2: PricePart[] | undefined | null
): boolean => {
  // 处理 null 或 undefined 情况
  if (!parts1 && !parts2) return true;
  if (!parts1 || !parts2) return false;

  // 长度不同，直接返回 false
  if (parts1.length !== parts2.length) return false;

  // 比较每个价格部分
  return parts1.every((part1, index) =>
    arePricePartsEqual(part1, parts2[index])
  );
};
